<template>
  <div class="w-full max-w-6xl mx-auto space-y-4 text-gray-800">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Ceremony Source Workflow</h1>
        <p class="text-sm text-gray-600">Choose how footage arrives, hand over instructions, review recordings, and approve the source.</p>
      </div>
      <router-link
        class="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-sm"
        to="/admin/upload"
      >
        Open upload page
      </router-link>
    </div>

    <section class="border border-gray-200 bg-white rounded-md p-3">
      <div class="grid grid-cols-1 md:grid-cols-5 gap-2">
        <label class="text-sm md:col-span-2">
          University
          <select
            class="w-full h-10 px-3 border rounded-md bg-white"
            v-model="selectedUniversityId"
            @change="loadItems"
          >
            <option disabled value="">Select University</option>
            <option v-for="university in universities" :key="university.id" :value="university.id">
              {{ university.name }}
            </option>
          </select>
        </label>
        <label class="text-sm">
          Source status
          <select class="w-full h-10 px-3 border rounded-md bg-white" v-model="statusFilter" @change="loadItems">
            <option value="">All statuses</option>
            <option value="not_configured">No source chosen</option>
            <option value="ready_for_encoder">Ready for AV team</option>
            <option value="configured">Configured</option>
            <option value="queued">Queued</option>
            <option value="dispatched">Dispatched</option>
            <option value="running">Running</option>
            <option value="uploaded">Uploaded</option>
            <option value="processing">Processing</option>
            <option value="available">Ready to review</option>
            <option value="selected">Selected</option>
            <option value="completed">Completed</option>
            <option value="needs_attention">Needs attention</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <label class="text-sm">
          Source
          <select class="w-full h-10 px-3 border rounded-md bg-white" v-model="inputModeFilter" @change="loadItems">
            <option value="">All sources</option>
            <option value="upload">Upload / not chosen</option>
            <option value="stream">AV team streams to us</option>
            <option value="external_url">Live URL recording</option>
          </select>
        </label>
        <label class="text-sm">
          Search
          <input
            class="w-full h-10 px-3 border rounded-md"
            type="search"
            v-model.trim="search"
            @keyup.enter="loadItems"
            placeholder="Ceremony"
          />
        </label>
      </div>
      <div class="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-sm"
          @click="loadItems"
          :disabled="loading || !selectedUniversityId"
        >
          Refresh
        </button>
        <button
          type="button"
          class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-sm"
          @click="clearFilters"
        >
          Clear filters
        </button>
      </div>
    </section>

    <section class="grid grid-cols-2 lg:grid-cols-4 gap-2">
      <button type="button" class="text-left border border-gray-200 bg-white rounded-md p-3 hover:bg-gray-50" @click="applyQuickFilter('not_configured')">
        <div class="text-xs uppercase text-gray-500">Need source</div>
        <div class="text-2xl font-semibold text-gray-900">{{ summaryCounts.noSource }}</div>
      </button>
      <button type="button" class="text-left border border-gray-200 bg-white rounded-md p-3 hover:bg-gray-50" @click="applyQuickFilter('ready_for_encoder')">
        <div class="text-xs uppercase text-gray-500">Ready for AV</div>
        <div class="text-2xl font-semibold text-gray-900">{{ summaryCounts.readyForAv }}</div>
      </button>
      <button type="button" class="text-left border border-gray-200 bg-white rounded-md p-3 hover:bg-gray-50" @click="applyQuickFilter('available')">
        <div class="text-xs uppercase text-gray-500">Ready to review</div>
        <div class="text-2xl font-semibold text-gray-900">{{ summaryCounts.readyToReview }}</div>
      </button>
      <button type="button" class="text-left border border-gray-200 bg-white rounded-md p-3 hover:bg-gray-50" @click="applyQuickFilter('needs_attention')">
        <div class="text-xs uppercase text-gray-500">Need attention</div>
        <div class="text-2xl font-semibold text-red-700">{{ summaryCounts.needsAttention }}</div>
      </button>
    </section>

    <div v-if="errorMessage" class="border border-red-200 bg-red-50 text-red-800 rounded-md p-3 text-sm">
      {{ errorMessage }}
    </div>

    <section class="border border-gray-200 bg-white rounded-md overflow-hidden">
      <div class="px-3 py-2 border-b border-gray-200 flex flex-wrap justify-between gap-2 text-sm">
        <span>{{ loading ? 'Loading ceremonies...' : `${filteredItems.length} ceremonies` }}</span>
        <span v-if="summaryCounts.needsAttention" class="font-medium text-red-700">{{ summaryCounts.needsAttention }} need attention</span>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th class="px-3 py-2 font-medium">Ceremony</th>
              <th class="px-3 py-2 font-medium">Source</th>
              <th class="px-3 py-2 font-medium">Status</th>
              <th class="px-3 py-2 font-medium">Next step</th>
              <th class="px-3 py-2 font-medium">Time</th>
              <th class="px-3 py-2 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!loading && filteredItems.length === 0">
              <td colspan="6" class="px-3 py-8 text-center text-gray-500">No ceremonies match these filters.</td>
            </tr>
            <tr
              v-for="item in filteredItems"
              :key="item.ceremony_id"
              class="border-t border-gray-100 align-top hover:bg-gray-50"
              :class="selectedItem && selectedItem.ceremony_id === item.ceremony_id ? 'bg-indigo-50' : ''"
            >
              <td class="px-3 py-3">
                <div class="font-medium text-gray-900">{{ ceremonyLabel(item) }}</div>
                <div class="text-xs text-gray-600">{{ formatDateTime(item.ceremony_time, item.schedule_timezone) }}</div>
              </td>
              <td class="px-3 py-3">
                <div>{{ workflowSourceLabel(item) }}</div>
                <div class="text-xs text-gray-600">{{ workflowSourceDetail(item) }}</div>
              </td>
              <td class="px-3 py-3">
                <span :class="workflowStateClass(item)">{{ workflowStateLabel(item) }}</span>
                <div v-if="item.stream_health && item.stream_health.review_required" class="mt-1 max-w-xs text-xs font-medium text-red-700">
                  Stream interruption detected
                </div>
                <div v-if="item.latest_job && item.latest_job.error_message" class="mt-1 max-w-xs break-words text-xs text-red-700">
                  {{ item.latest_job.error_message }}
                </div>
              </td>
              <td class="px-3 py-3">
                <div class="font-medium text-gray-900">{{ nextStepLabel(item) }}</div>
                <div class="text-xs text-gray-600">{{ nextStepHelp(item) }}</div>
              </td>
              <td class="px-3 py-3">
                <div>{{ workflowTimeLabel(item) }}</div>
                <div v-if="item.schedule_timezone" class="text-xs text-gray-600">{{ item.schedule_timezone }}</div>
              </td>
              <td class="px-3 py-3">
                <div class="flex flex-wrap gap-2">
                  <button
                    type="button"
                    class="px-3 py-2 rounded-md border text-sm"
                    :class="primaryActionClass(item)"
                    @click="openWorkflow(item)"
                  >
                    {{ primaryActionLabel(item) }}
                  </button>
                  <button
                    v-if="item.latest_job && item.latest_job.error_message"
                    type="button"
                    class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-sm"
                    @click="copyError(item)"
                  >
                    Copy error
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="selectedItem" ref="workflowPanel" class="border border-gray-200 bg-white rounded-md overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">{{ ceremonyLabel(selectedItem) }}</h2>
          <p class="text-sm text-gray-600">{{ workflowStateLabel(selectedItem) }} · {{ nextStepLabel(selectedItem) }}</p>
        </div>
        <button
          type="button"
          class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-sm"
          @click="closeWorkflow"
        >
          Close
        </button>
      </div>

      <div v-if="detailLoading" class="p-4 text-sm text-gray-600">Loading ceremony workflow...</div>
      <div v-else-if="detailError" class="p-4 text-sm text-red-700">{{ detailError }}</div>
      <div v-else class="p-4 space-y-4">
        <div v-if="actionMessage" class="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {{ actionMessage }}
        </div>

        <div v-if="selectedStreamHealth && selectedStreamHealth.review_required" class="rounded-md border-2 border-red-400 bg-red-50 p-4 text-red-900">
          <div class="font-semibold">Stream interruption detected — review the recording before using it</div>
          <p class="mt-1 text-sm">
            IVS stopped receiving media from the encoder at {{ streamHealthStartedLabel }}.
            <span v-if="selectedStreamHealth.state === 'recovered'">
              Delivery later recovered at {{ streamHealthRecoveredLabel }}, but the recording may contain missing footage.
            </span>
            <span v-else>
              Delivery has not reported a recovery event. The recording may be incomplete.
            </span>
          </p>
          <p class="mt-2 text-sm font-medium">Preview the complete recording and replace the source if footage is missing.</p>
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-md bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-75"
              :disabled="resolvingStreamHealth"
              @click="resolveStreamHealth('recording_reviewed')"
            >
              {{ resolvingStreamHealth ? 'Saving...' : 'Recording checked — mark reviewed' }}
            </button>
            <button
              type="button"
              class="rounded-md border border-red-400 bg-white px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-100 disabled:opacity-75"
              :disabled="resolvingStreamHealth"
              @click="resolveStreamHealth('source_replaced')"
            >
              Source replaced — resolve warning
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div class="rounded-md border border-gray-200 p-3">
            <div class="text-xs uppercase text-gray-500">Source</div>
            <div class="mt-1 font-medium text-gray-900">{{ workflowSourceLabel(selectedItem) }}</div>
            <div class="text-xs text-gray-600">{{ workflowSourceDetail(selectedItem) }}</div>
          </div>
          <div class="rounded-md border border-gray-200 p-3">
            <div class="text-xs uppercase text-gray-500">Status</div>
            <div class="mt-1">
              <span :class="workflowStateClass(selectedItem)">{{ workflowStateLabel(selectedItem) }}</span>
            </div>
          </div>
          <div class="rounded-md border border-gray-200 p-3">
            <div class="text-xs uppercase text-gray-500">When</div>
            <div class="mt-1 font-medium text-gray-900">{{ workflowTimeLabel(selectedItem) }}</div>
            <div class="text-xs text-gray-600">{{ selectedItem.schedule_timezone || 'UTC' }}</div>
          </div>
          <div class="rounded-md border border-gray-200 p-3">
            <div class="text-xs uppercase text-gray-500">Next</div>
            <div class="mt-1 font-medium text-gray-900">{{ primaryActionLabel(selectedItem) }}</div>
          </div>
        </div>

        <div v-if="selectedCurrentSource" class="space-y-3">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 class="font-semibold text-gray-900">Current source</h3>
              <p class="text-sm text-gray-600">{{ sourceTypeLabel(selectedCurrentSource) }} for {{ sourceUsagesLabel(selectedCurrentSource) }}</p>
            </div>
            <router-link
              class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-sm"
              :to="setupLink(selectedItem)"
            >
              Replace source
            </router-link>
          </div>
          <div class="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div><span class="font-medium">Status:</span> {{ sourceStatusLabel(selectedCurrentSource) }}</div>
              <div><span class="font-medium">Size:</span> {{ formatBytes(selectedCurrentSource.file_size_bytes) }}</div>
              <div><span class="font-medium">Clip processing:</span> {{ sourceProcessLabel(selectedCurrentSource.clip_processing_status) }}</div>
              <div><span class="font-medium">Digital download:</span> {{ sourceProcessLabel(selectedCurrentSource.digital_download_status) }}</div>
              <div><span class="font-medium">Wide ref:</span> {{ sourceReferenceLabel(selectedCurrentSource, 'wide') }}</div>
              <div><span class="font-medium">Close ref:</span> {{ sourceReferenceLabel(selectedCurrentSource, 'close') }}</div>
              <div><span class="font-medium">Clip window:</span> {{ sourceClipWindowLabel(selectedCurrentSource) }}</div>
              <div><span class="font-medium">Created:</span> {{ formatDateTime(selectedCurrentSource.created_at, selectedSettings && selectedSettings.schedule_timezone) || '-' }}</div>
            </div>
          </div>
        </div>

        <div v-if="workflowStep(selectedItem) === 'choose'" class="space-y-3">
          <h3 class="font-semibold text-gray-900">Choose how the footage will arrive</h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <router-link :to="setupLink(selectedItem, 'file')" class="block rounded-md border border-gray-300 p-4 bg-white hover:bg-gray-50">
              <div class="font-semibold text-gray-900">Upload file now</div>
              <p class="mt-1 text-sm text-gray-600">I already have the MP4.</p>
              <div class="mt-3 inline-flex px-3 py-2 rounded-md bg-indigo-600 text-white text-sm">Upload file</div>
            </router-link>
            <router-link :to="setupLink(selectedItem, 'stream')" class="block rounded-md border border-gray-300 p-4 bg-white hover:bg-gray-50">
              <div class="font-semibold text-gray-900">Create live ingest</div>
              <p class="mt-1 text-sm text-gray-600">The AV team will stream directly to us.</p>
              <div class="mt-3 inline-flex px-3 py-2 rounded-md bg-indigo-600 text-white text-sm">Create AV instructions</div>
            </router-link>
            <router-link :to="setupLink(selectedItem, 'external_url')" class="block rounded-md border border-gray-300 p-4 bg-white hover:bg-gray-50">
              <div class="font-semibold text-gray-900">Record live URL</div>
              <p class="mt-1 text-sm text-gray-600">The live stream is hosted somewhere else.</p>
              <div class="mt-3 inline-flex px-3 py-2 rounded-md bg-indigo-600 text-white text-sm">Set live URL</div>
            </router-link>
          </div>
        </div>

        <div v-if="selectedSettings && selectedSettings.input_mode === 'stream'" class="space-y-3">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 class="font-semibold text-gray-900">AV setup details</h3>
              <p class="text-sm text-gray-600">Check the stream key, then give the encoder details to the AV team.</p>
            </div>
            <button
              type="button"
              class="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 text-sm"
              @click="copyAvInstructions"
            >
              {{ streamKeyHidden ? 'Copy setup summary' : 'Copy AV instructions' }}
            </button>
          </div>
          <div class="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm space-y-2">
            <div><span class="font-medium">Ceremony:</span> {{ ceremonyLabel(selectedItem) }}</div>
            <div><span class="font-medium">Recording window:</span> {{ detailScheduleLabel }}</div>
            <div><span class="font-medium">Timezone:</span> {{ selectedSettings.schedule_timezone || 'UTC' }}</div>
            <div v-if="selectedSettings.stream_key"><span class="font-medium">Stream key:</span> {{ selectedSettings.stream_key }}</div>
            <div v-else-if="selectedSettings.stream_key_hint"><span class="font-medium">Stream key:</span> ends {{ selectedSettings.stream_key_hint }}</div>
            <div v-if="streamKeyHidden" class="rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
              Full stream key is not visible here. If the AV team does not already have it, open setup and rotate the key before sending instructions.
            </div>
            <div v-if="streamStatusLabel" class="flex flex-wrap items-center gap-2">
              <span class="font-medium">Stream status:</span>
              <span :class="streamStatusClass">{{ streamStatusLabel }}</span>
              <span v-if="streamStatusDetail" class="text-gray-600">{{ streamStatusDetail }}</span>
            </div>
            <div v-if="Object.keys(ingestUrls).length" class="pt-2 space-y-2">
              <div v-for="(url, protocol) in ingestUrls" :key="protocol" class="flex flex-wrap items-center gap-2">
                <span class="font-medium uppercase">{{ protocol }}</span>
                <code class="break-all rounded bg-white px-2 py-1 border border-gray-200">{{ url }}</code>
                <button type="button" class="px-2 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50" @click="copyText(url, `${protocol.toUpperCase()} URL copied`)">
                  Copy
                </button>
              </div>
            </div>
            <div v-if="streamPreviewUrl" class="pt-2">
              <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span class="font-medium">Preview</span>
                <a :href="streamPreviewUrl" target="_blank" rel="noopener" class="text-indigo-700 hover:underline">Open preview</a>
              </div>
              <video class="w-full max-h-72 rounded border border-gray-200 bg-black" controls muted playsinline>
                <source :src="streamPreviewUrl" type="application/x-mpegURL" />
              </video>
            </div>
          </div>
        </div>

        <div v-if="reviewableJobs.length" class="space-y-3">
          <h3 class="font-semibold text-gray-900">{{ approvableJobs.length ? 'Review footage' : 'Problem details' }}</h3>
          <div v-if="approvableJobs.length" class="rounded-md border border-gray-200 bg-gray-50 p-3">
            <div class="font-medium text-gray-900">Use approved source for</div>
            <div class="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <label class="flex items-center gap-2 rounded border border-gray-300 bg-white p-2">
                <input type="checkbox" value="clips" v-model="approvalUsages" />
                <span>Personalised clips</span>
              </label>
              <label class="flex items-center gap-2 rounded border border-gray-300 bg-white p-2">
                <input type="checkbox" value="digital_download" v-model="approvalUsages" />
                <span>Full ceremony digital download</span>
              </label>
            </div>
            <div v-if="!approvalUsages.length" class="mt-2 text-sm text-red-700">
              Choose at least one output before approving this source.
            </div>
            <div class="mt-4">
              <div class="font-medium text-gray-900">Clip timing and camera references</div>
              <div class="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                <label class="block rounded border border-gray-300 bg-white p-2">
                  <span class="block text-gray-700">In point seconds</span>
                  <input v-model="approvalTiming.startSeconds" type="number" min="0" step="0.1" class="mt-1 w-full rounded border border-gray-300 px-2 py-1" />
                </label>
                <label class="block rounded border border-gray-300 bg-white p-2">
                  <span class="block text-gray-700">Out point seconds</span>
                  <input v-model="approvalTiming.endSeconds" type="number" min="0" step="0.1" class="mt-1 w-full rounded border border-gray-300 px-2 py-1" />
                </label>
                <label class="block rounded border border-gray-300 bg-white p-2">
                  <span class="block text-gray-700">Wide ref seconds</span>
                  <input v-model="approvalReferences.wide" type="number" min="0" step="0.1" class="mt-1 w-full rounded border border-gray-300 px-2 py-1" />
                </label>
                <label class="block rounded border border-gray-300 bg-white p-2">
                  <span class="block text-gray-700">Close ref seconds</span>
                  <input v-model="approvalReferences.close" type="number" min="0" step="0.1" class="mt-1 w-full rounded border border-gray-300 px-2 py-1" />
                </label>
              </div>
              <div v-if="approvalTimingError" class="mt-2 text-sm text-red-700">
                {{ approvalTimingError }}
              </div>
              <div v-else-if="approvalReferenceError" class="mt-2 text-sm text-red-700">
                {{ approvalReferenceError }}
              </div>
            </div>
          </div>
          <div class="space-y-2">
            <div v-for="job in reviewableJobs" :key="job.id" class="rounded-md border border-gray-200 p-3">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div class="font-medium text-gray-900">{{ reviewJobLabel(job) }}</div>
                  <div class="text-sm text-gray-600">{{ reviewJobSummary(job) }}</div>
                  <div v-if="job.error_message" class="mt-1 text-sm text-red-700">{{ job.error_message }}</div>
                </div>
                <button
                  v-if="canApproveJob(job)"
                  type="button"
                  class="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-75 text-sm"
                  :disabled="approvingJobId === job.id || !approvalUsages.length || approvalValidationError.length > 0"
                  @click="approveRecording(job)"
                >
                  {{ approvingJobId === job.id ? 'Approving...' : 'Approve this source' }}
                </button>
              </div>
              <div v-if="canApproveJob(job)" class="mt-3 space-y-3">
                <div class="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-75 text-sm"
                    :disabled="reviewPreviewLoadingJobId === job.id"
                    @click="loadRecordingPreview(job)"
                  >
                    {{ reviewPreviewLoadingJobId === job.id ? 'Loading preview...' : 'Load recording preview' }}
                  </button>
                  <span v-if="reviewPreviewJobId === job.id && reviewPreviewUrl" class="text-sm text-gray-600">
                    Scrub the recording, then mark the times below.
                  </span>
                </div>
                <div v-if="reviewPreviewJobId === job.id && reviewPreviewError" class="text-sm text-red-700">
                  {{ reviewPreviewError }}
                </div>
                <div v-if="reviewPreviewJobId === job.id && reviewPreviewUrl" class="space-y-3">
                  <video-player
                    :options="reviewPreviewOptions"
                    ref="reviewVideoPlayer"
                  ></video-player>
                  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                    <button type="button" class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50" @click="markApprovalTiming('start')">
                      Set in point
                    </button>
                    <button type="button" class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50" @click="markApprovalTiming('end')">
                      Set out point
                    </button>
                    <button type="button" class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50" @click="markApprovalReference('wide')">
                      Set wide ref
                    </button>
                    <button type="button" class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50" @click="markApprovalReference('close')">
                      Set close ref
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="workflowStep(selectedItem) !== 'choose'" class="flex flex-wrap gap-2">
          <router-link
            class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-sm"
            :to="setupLink(selectedItem)"
          >
            Edit setup
          </router-link>
          <router-link
            v-if="streamKeyHidden"
            class="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 text-sm"
            :to="setupLink(selectedItem, 'stream')"
          >
            Open setup to rotate key
          </router-link>
          <button
            v-if="selectedErrorText"
            type="button"
            class="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-sm"
            @click="copyText(selectedErrorText, 'Error copied')"
          >
            Copy problem details
          </button>
        </div>

        <details class="rounded-md border border-gray-200 p-3">
          <summary class="cursor-pointer text-sm font-medium text-gray-800">Technical history</summary>
          <div class="mt-3 space-y-3 text-sm">
            <div>
              <div class="mb-1 font-medium text-gray-900">Source attempts</div>
              <div v-if="!selectedSourceHistory.length" class="text-gray-600">No source uploads yet.</div>
              <div v-for="source in selectedSourceHistory" :key="source.id" class="border-t border-gray-100 pt-2 first:border-t-0 first:pt-0">
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <div class="font-medium text-gray-900">{{ sourceTypeLabel(source) }}</div>
                  <span class="inline-flex rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">{{ sourceStatusLabel(source) }}</span>
                </div>
                <div class="text-gray-600">
                  {{ source.original_filename || source.id }} - {{ sourceUsagesLabel(source) }} - {{ formatBytes(source.file_size_bytes) }}
                </div>
              </div>
            </div>
            <div>
              <div class="mb-1 font-medium text-gray-900">Input jobs</div>
            <div v-if="!selectedJobs.length" class="text-gray-600">No source jobs yet.</div>
            <div v-for="job in selectedJobs" :key="job.id" class="border-t border-gray-100 pt-2 first:border-t-0 first:pt-0">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="font-medium text-gray-900">{{ jobTypeLabel(job.job_type) }}</div>
                <span :class="rawStatusClass(job.status)">{{ rawStatusLabel(job.status) }}</span>
              </div>
              <div class="text-gray-600">{{ jobTechnicalSummary(job) }}</div>
              <div v-if="job.error_message" class="text-red-700 break-words">{{ job.error_message }}</div>
            </div>
            </div>
          </div>
        </details>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import '../../plugins/video-player'
import { trackAdminEvent } from '../../admin/activity.js'

export default {
  name: 'SourceInputs',
  data() {
    return {
      universities: [],
      selectedUniversityId: '',
      statusFilter: '',
      inputModeFilter: '',
      search: '',
      items: [],
      loading: false,
      errorMessage: '',
      selectedItem: null,
      selectedDetail: null,
      detailLoading: false,
      detailError: '',
      actionMessage: '',
      approvingJobId: '',
      detailRequestId: 0,
      approvalUsages: ['clips'],
      approvalTiming: {
        startSeconds: '',
        endSeconds: '',
      },
      approvalReferences: {
        wide: '',
        close: '',
      },
      reviewPreviewJobId: '',
      reviewPreviewUrl: '',
      reviewPreviewLoadingJobId: '',
      reviewPreviewError: '',
      resolvingStreamHealth: false,
    }
  },
  computed: {
    filteredItems() {
      return this.items
    },
    summaryCounts() {
      return this.items.reduce((counts, item) => {
        const step = this.workflowStep(item)
        if (step === 'choose') counts.noSource += 1
        if (step === 'handoff') counts.readyForAv += 1
        if (step === 'review') counts.readyToReview += 1
        if (step === 'attention') counts.needsAttention += 1
        return counts
      }, {
        noSource: 0,
        readyForAv: 0,
        readyToReview: 0,
        needsAttention: 0,
      })
    },
    sourceInputApiBase() {
      return (process.env.VUE_APP_SOURCE_INPUT_API_BASE_URL || '/source-input').replace(/\/$/, '')
    },
    selectedSettings() {
      return this.selectedDetail && this.selectedDetail.settings ? this.selectedDetail.settings : null
    },
    selectedJobs() {
      return this.selectedDetail && Array.isArray(this.selectedDetail.jobs) ? this.selectedDetail.jobs : []
    },
    selectedCurrentSource() {
      return this.selectedDetail && this.selectedDetail.current_source ? this.selectedDetail.current_source : null
    },
    selectedSourceHistory() {
      return this.selectedDetail && Array.isArray(this.selectedDetail.source_history) ? this.selectedDetail.source_history : []
    },
    ingestUrls() {
      return (this.selectedSettings && this.selectedSettings.ingest_urls) || {}
    },
    streamPreviewUrl() {
      return (this.selectedSettings && this.selectedSettings.preview_url) || ''
    },
    streamStatus() {
      return (this.selectedSettings && this.selectedSettings.stream_status) || null
    },
    selectedStreamHealth() {
      return (this.selectedSettings && this.selectedSettings.stream_health) ||
        (this.selectedItem && this.selectedItem.stream_health) || null
    },
    streamHealthStartedLabel() {
      if (!this.selectedStreamHealth) return 'an unknown time'
      return this.formatDateTime(
        this.selectedStreamHealth.starvation_started_at,
        (this.selectedSettings && this.selectedSettings.schedule_timezone) || 'UTC'
      ) || 'an unknown time'
    },
    streamHealthRecoveredLabel() {
      if (!this.selectedStreamHealth) return 'an unknown time'
      return this.formatDateTime(
        this.selectedStreamHealth.recovered_at,
        (this.selectedSettings && this.selectedSettings.schedule_timezone) || 'UTC'
      ) || 'an unknown time'
    },
    streamStatusLabel() {
      if (!this.selectedSettings || this.selectedSettings.input_mode !== 'stream') return ''
      if (!this.streamStatus || this.streamStatus.connected === null || this.streamStatus.state === 'unknown') return 'Unknown'
      return this.streamStatus.connected ? 'Connected' : 'Not connected'
    },
    streamStatusClass() {
      const base = 'inline-flex rounded px-2 py-1 text-xs font-medium'
      if (!this.streamStatus || this.streamStatus.connected === null || this.streamStatus.state === 'unknown') return `${base} bg-gray-100 text-gray-800`
      if (this.streamStatus.connected) return `${base} bg-green-100 text-green-800`
      return `${base} bg-amber-100 text-amber-800`
    },
    streamStatusDetail() {
      if (!this.streamStatus || !this.streamStatus.connected) return ''
      const parts = []
      if (this.streamStatus.health) parts.push(`Health: ${this.streamStatus.health}`)
      if (this.streamStatus.start_time) parts.push(`Started ${this.formatDateTime(this.streamStatus.start_time, this.selectedSettings.schedule_timezone)}`)
      return parts.join(' - ')
    },
    approvalTimingError() {
      const hasStart = this.approvalTiming.startSeconds !== ''
      const hasEnd = this.approvalTiming.endSeconds !== ''
      if (!hasStart && !hasEnd) return ''
      const start = this.numberOrNull(this.approvalTiming.startSeconds)
      const end = this.numberOrNull(this.approvalTiming.endSeconds)
      if (start === null || end === null) return 'Enter both in and out points, or leave both blank.'
      if (start < 0 || end < 0) return 'In and out points must be zero or higher.'
      if (end <= start) return 'Out point must be after the in point.'
      return ''
    },
    approvalReferenceError() {
      if (!this.approvalUsages.includes('clips')) return ''
      if (
        this.numberOrNull(this.approvalReferences.wide) === null ||
        this.numberOrNull(this.approvalReferences.close) === null
      ) {
        return 'Set both wide and close references before approving clips.'
      }
      return ''
    },
    approvalValidationError() {
      return this.approvalTimingError || this.approvalReferenceError
    },
    reviewPreviewOptions() {
      return {
        sources: [
          {
            type: 'application/x-mpegURL',
            src: this.reviewPreviewUrl,
          },
        ],
        aspectRatio: '16:9',
        responsive: true,
        controls: true,
      }
    },
    reviewableJobs() {
      return this.selectedJobs.filter(job => (
        job.status === 'available' ||
        job.status === 'failed' ||
        job.status === 'completed' ||
        job.status === 'selected'
      ))
    },
    approvableJobs() {
      return this.reviewableJobs.filter(job => this.canApproveJob(job))
    },
    detailScheduleLabel() {
      if (!this.selectedSettings) return '-'
      const start = this.formatDateTime(this.selectedSettings.scheduled_start_at, this.selectedSettings.schedule_timezone)
      const end = this.formatDateTime(this.selectedSettings.scheduled_end_at, this.selectedSettings.schedule_timezone)
      if (start && end) return `${start} to ${end}`
      if (start) return `Starts ${start}`
      if (end) return `Ends ${end}`
      return '-'
    },
    selectedErrorText() {
      const failed = this.selectedJobs.find(job => job.error_message)
      return failed ? failed.error_message : ''
    },
    streamKeyHidden() {
      return Boolean(
        this.selectedSettings &&
        this.selectedSettings.input_mode === 'stream' &&
        this.selectedSettings.stream_key_hint &&
        !this.selectedSettings.stream_key
      )
    },
  },
  async created() {
    trackAdminEvent('admin_tool_opened', { toolKey: 'source_inputs' })
    await this.loadUniversities()
  },
  beforeDestroy() {
    this.clearRecordingPreview()
  },
  methods: {
    async loadUniversities() {
      try {
        const response = await axios.get('/universities')
        this.universities = Array.isArray(response.data) ? response.data : []
        const requestedUniversityId = this.$route.query.universityId
        const requestedUniversity = this.universities.find(item => item.id === requestedUniversityId)
        if (!this.selectedUniversityId && this.universities.length) {
          this.selectedUniversityId = requestedUniversity ? requestedUniversity.id : this.universities[0].id
          await this.loadItems()
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'Could not load universities.'
      }
    },
    async loadItems() {
      if (!this.selectedUniversityId) return
      this.loading = true
      this.errorMessage = ''
      try {
        const params = {}
        if (this.statusFilter) params.status = this.statusFilter
        if (this.inputModeFilter) params.inputMode = this.inputModeFilter
        if (this.search) params.search = this.search
        const response = await axios.get(
          `${this.sourceInputApiBase}/universities/${this.selectedUniversityId}/inputs`,
          { params }
        )
        this.items = Array.isArray(response.data?.items) ? response.data.items : []
        if (this.selectedItem) {
          const updated = this.items.find(item => item.ceremony_id === this.selectedItem.ceremony_id)
          if (updated) {
            this.selectedItem = updated
          } else {
            this.closeWorkflow()
          }
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'Could not load ceremonies.'
      } finally {
        this.loading = false
      }
    },
    clearFilters() {
      this.statusFilter = ''
      this.inputModeFilter = ''
      this.search = ''
      this.loadItems()
    },
    applyQuickFilter(status) {
      this.statusFilter = this.statusFilter === status ? '' : status
      this.loadItems()
    },
    ceremonyLabel(item) {
      return item.ceremony_name || item.ceremony_id
    },
    sourceTypeLabel(source) {
      const type = ((source && source.type) || '').toUpperCase()
      if (type === 'STREAM') return 'Recorded stream'
      if (type === 'LIVE_URL') return 'Recorded live URL'
      if (type === 'PUBLISHED_VIDEO') return 'Imported video'
      if (type === 'FILE') return 'Uploaded file'
      return 'Source footage'
    },
    sourceStatusLabel(source) {
      if (!source) return '-'
      return source.status || 'Unknown'
    },
    sourceProcessLabel(value) {
      if (!value) return '-'
      return String(value).replace(/_/g, ' ')
    },
    sourceUsagesLabel(source) {
      const usages = (source && source.usages) || []
      if (usages.includes('clips') && usages.includes('digital_download')) return 'clips + digital download'
      if (usages.includes('digital_download')) return 'digital download'
      if (usages.includes('clips')) return 'clips'
      return 'source'
    },
    sourceReferenceLabel(source, key) {
      const value = source && source.references ? source.references[key] : null
      return value === null || value === undefined ? '-' : this.formatSeconds(value)
    },
    sourceClipWindowLabel(source) {
      const clipWindow = source && source.clip_window
      if (!clipWindow) return '-'
      const start = clipWindow.start_seconds
      const end = clipWindow.end_seconds
      if (start === null || start === undefined || end === null || end === undefined) return '-'
      return `${this.formatSeconds(start)} to ${this.formatSeconds(end)}`
    },
    formatSeconds(value) {
      const seconds = Number(value)
      if (!Number.isFinite(seconds) || seconds < 0) return '-'
      const wholeSeconds = Math.floor(seconds)
      const minutes = Math.floor(wholeSeconds / 60)
      const remaining = wholeSeconds % 60
      const decimal = seconds % 1
      const suffix = decimal ? `.${String(Math.round(decimal * 10))}` : ''
      return `${minutes}:${String(remaining).padStart(2, '0')}${suffix}`
    },
    formatBytes(value) {
      const bytes = Number(value)
      if (!Number.isFinite(bytes) || bytes <= 0) return '-'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let size = bytes
      let unitIndex = 0
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex += 1
      }
      return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
    },
    workflowStep(item) {
      const latestStatus = item.latest_job && item.latest_job.status
      const source = item.current_source
      if (item.status === 'needs_attention' || item.status === 'failed' || latestStatus === 'failed') return 'attention'
      if (this.canApproveJob(item.latest_job) || (item.status === 'available' && item.provider === 'ivs')) return 'review'
      if (latestStatus === 'selected') return 'approved'
      if (source) {
        if (item.status === 'queued') return 'waiting'
        if (item.status === 'processing' || item.status === 'uploaded' || item.status === 'completed') return 'processing'
        return 'details'
      }
      if (item.status === 'not_configured') return 'choose'
      if (item.status === 'ready_for_encoder' || (item.input_mode === 'stream' && item.stream_key_hint && !latestStatus)) return 'handoff'
      if (item.status === 'running' || latestStatus === 'running') return 'active'
      if (item.status === 'queued' || item.status === 'dispatched' || latestStatus === 'queued' || latestStatus === 'dispatched') return 'waiting'
      if (item.status === 'completed' || latestStatus === 'completed') return 'processing'
      if (item.status === 'configured') return item.input_mode === 'stream' ? 'handoff' : 'waiting'
      return 'details'
    },
    workflowStateLabel(item) {
      const labels = {
        choose: 'No source chosen',
        handoff: 'Ready to give AV team',
        waiting: item.input_mode === 'stream' ? 'Waiting for stream' : 'Waiting',
        active: item.input_mode === 'stream' ? 'Recording' : 'Importing',
        review: 'Ready to review',
        approved: 'Source approved',
        processing: item.current_source ? 'Source uploaded' : 'Processing clips',
        attention: 'Needs attention',
        details: 'Check status',
      }
      return labels[this.workflowStep(item)] || 'Check status'
    },
    workflowStateClass(item) {
      const base = 'inline-flex rounded px-2 py-1 text-xs font-medium'
      const step = this.workflowStep(item)
      if (step === 'attention') return `${base} bg-red-100 text-red-800`
      if (step === 'review') return `${base} bg-yellow-100 text-yellow-800`
      if (step === 'handoff' || step === 'approved' || step === 'processing') return `${base} bg-green-100 text-green-800`
      if (step === 'active' || step === 'waiting') return `${base} bg-blue-100 text-blue-800`
      return `${base} bg-gray-100 text-gray-800`
    },
    workflowSourceLabel(item) {
      if (item.current_source) return this.sourceTypeLabel(item.current_source)
      if (item.input_mode === 'stream') return 'AV team streams to us'
      if (item.input_mode === 'external_url') return 'Record a live URL'
      if (item.status === 'not_configured') return 'Not chosen yet'
      return 'Uploaded file'
    },
    workflowSourceDetail(item) {
      if (item.current_source) {
        const parts = [
          this.sourceUsagesLabel(item.current_source),
          this.formatBytes(item.current_source.file_size_bytes),
        ].filter(part => part && part !== '-')
        return parts.join(' - ') || this.sourceStatusLabel(item.current_source)
      }
      if (item.input_mode === 'stream') {
        if (item.stream_key_hint) return `Stream key ends ${item.stream_key_hint}`
        return 'Ingest setup required'
      }
      if (item.input_mode === 'external_url') {
        return item.source_url_present ? 'URL saved' : 'URL required'
      }
      return item.status === 'not_configured' ? 'Choose upload, live ingest, or live URL' : 'MP4 source'
    },
    nextStepLabel(item) {
      const labels = {
        choose: 'Choose how footage arrives',
        handoff: 'Check AV setup',
        waiting: 'Wait for footage',
        active: 'Check progress',
        review: 'Review and approve',
        approved: 'Wait for processing',
        processing: item.current_source ? 'View source' : 'Wait for processing',
        attention: 'Fix the problem',
        details: 'Check details',
      }
      return labels[this.workflowStep(item)] || 'Check details'
    },
    nextStepHelp(item) {
      const step = this.workflowStep(item)
      if (step === 'choose') return 'Pick upload, live ingest, or URL.'
      if (step === 'handoff') return 'Make sure the full key is available before sending.'
      if (step === 'review') return 'Confirm this is the footage to use.'
      if (step === 'attention') return 'Open the workflow to see the safe error.'
      if (step === 'active') return 'The system is recording or importing.'
      if (step === 'waiting') return 'The source is scheduled or queued.'
      if (item.current_source) return 'Check the active source and previous attempts.'
      return 'Open the workflow for status.'
    },
    primaryActionLabel(item) {
      const labels = {
        choose: 'Choose source',
        handoff: 'AV setup',
        waiting: 'View status',
        active: 'Check progress',
        review: 'Review source',
        approved: 'View processing',
        processing: item.current_source ? 'View source' : 'View processing',
        attention: 'Fix problem',
        details: 'View details',
      }
      return labels[this.workflowStep(item)] || 'View details'
    },
    primaryActionClass(item) {
      const step = this.workflowStep(item)
      if (step === 'attention') return 'border-red-600 bg-red-600 text-white hover:bg-red-700'
      if (step === 'review' || step === 'choose' || step === 'handoff') return 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700'
      return 'border-gray-300 bg-white hover:bg-gray-50'
    },
    workflowTimeLabel(item) {
      const start = this.formatDateTime(item.scheduled_start_at || item.ceremony_time, item.schedule_timezone)
      const end = this.formatDateTime(item.scheduled_end_at, item.schedule_timezone)
      if (item.scheduled_start_at && end) return `${start} to ${end}`
      return start || '-'
    },
    setupLink(item, mode = null) {
      const query = {
        universityId: this.selectedUniversityId,
        ceremonyId: item.ceremony_id,
      }
      if (mode) query.sourceMode = mode
      return { path: '/admin/upload', query }
    },
    sourceInputUrl(item, path = '') {
      return `${this.sourceInputApiBase}/universities/${this.selectedUniversityId}/ceremonies/${item.ceremony_id}/inputs${path}`
    },
    summaryDetailForItem(item) {
      const jobs = item.latest_job ? [item.latest_job] : []
      const sourceHistory = item.current_source ? [item.current_source] : []
      return {
        settings: {
          input_mode: item.input_mode,
          stream_provider: item.provider,
          stream_key_present: item.stream_key_present,
          stream_key_hint: item.stream_key_hint,
          stream_protocols: item.stream_protocols || [],
          provider_status: item.provider_status || {},
          stream_health: item.stream_health || null,
          source_url_present: item.source_url_present,
          source_url_type: item.source_url_type,
          scheduled_start_at: item.scheduled_start_at,
          scheduled_end_at: item.scheduled_end_at,
          schedule_timezone: item.schedule_timezone,
        },
        jobs,
        current_source: item.current_source || null,
        source_history: sourceHistory,
      }
    },
    hydrateWorkflowItem(item, detail) {
      const settings = detail && detail.settings ? detail.settings : {}
      const jobs = detail && Array.isArray(detail.jobs) ? detail.jobs : []
      const currentSource = detail && detail.current_source ? detail.current_source : item.current_source
      return {
        ...item,
        input_mode: settings.input_mode || item.input_mode,
        provider: settings.stream_provider || item.provider,
        stream_key_present: settings.stream_key_present || item.stream_key_present,
        stream_key_hint: settings.stream_key_hint || item.stream_key_hint,
        stream_protocols: settings.stream_protocols || item.stream_protocols,
        provider_status: settings.provider_status || item.provider_status,
        stream_health: settings.stream_health || item.stream_health,
        source_url_present: settings.source_url_present || item.source_url_present,
        source_url_type: settings.source_url_type || item.source_url_type,
        scheduled_start_at: settings.scheduled_start_at || item.scheduled_start_at,
        scheduled_end_at: settings.scheduled_end_at || item.scheduled_end_at,
        schedule_timezone: settings.schedule_timezone || item.schedule_timezone,
        latest_job: jobs.length ? jobs[0] : item.latest_job,
        current_source: currentSource,
      }
    },
    async openWorkflow(item) {
      const requestId = this.detailRequestId + 1
      this.detailRequestId = requestId
      this.selectedItem = item
      this.selectedDetail = this.summaryDetailForItem(item)
      this.detailLoading = false
      this.detailError = ''
      this.actionMessage = ''
      this.$nextTick(() => this.scrollWorkflowIntoView())
      try {
        this.approvalUsages = ['clips']
        this.resetApprovalFields()
        this.clearRecordingPreview()
        const url = this.sourceInputUrl(item)
        const response = await axios.get(url)
        if (requestId !== this.detailRequestId || !this.selectedItem || this.selectedItem.ceremony_id !== item.ceremony_id) return
        const detail = response.data || {}
        this.selectedDetail = detail
        this.selectedItem = this.hydrateWorkflowItem(this.selectedItem, detail)
      } catch (error) {
        if (requestId !== this.detailRequestId) return
        this.detailError = error.response?.data?.error || error.message || 'Could not load the ceremony workflow.'
      } finally {
        if (requestId === this.detailRequestId) {
          this.detailLoading = false
          this.$nextTick(() => this.scrollWorkflowIntoView())
        }
      }
    },
    scrollWorkflowIntoView() {
      const element = this.$refs.workflowPanel
      if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    closeWorkflow() {
      this.detailRequestId += 1
      this.selectedItem = null
      this.selectedDetail = null
      this.detailError = ''
      this.actionMessage = ''
      this.approvalUsages = ['clips']
      this.resetApprovalFields()
      this.clearRecordingPreview()
    },
    isAvailableIvsRecordingSession(job) {
      return job && job.job_type === 'ivs_recording' && job.status === 'available'
    },
    canApproveJob(job) {
      return this.isAvailableIvsRecordingSession(job)
    },
    async approveRecording(job) {
      if (!this.selectedItem || !job || !job.id) return
      if (!this.approvalUsages.length) {
        this.detailError = 'Choose at least one output before approving this source.'
        return
      }
      if (this.approvalValidationError) {
        this.detailError = this.approvalValidationError
        return
      }
      this.approvingJobId = job.id
      this.actionMessage = ''
      try {
        const payload = {
          usages: this.approvalUsages,
          processingProfile: this.approvalProcessingProfile(),
          references: this.approvalReferencesPayload(),
        }
        const clipWindow = this.approvalClipWindowPayload()
        if (clipWindow) payload.clipWindow = clipWindow
        const response = await axios.post(
          this.sourceInputUrl(this.selectedItem, `/jobs/${job.id}/select`),
          payload
        )
        const updatedJob = response.data && response.data.job
        if (updatedJob) {
          const remaining = this.selectedJobs.filter(existing => existing.id !== updatedJob.id)
          this.selectedDetail = {
            ...(this.selectedDetail || {}),
            jobs: [updatedJob, ...remaining],
          }
        }
        this.actionMessage = 'Source approved. Processing has been queued.'
        await this.loadItems()
      } catch (error) {
        this.detailError = error.response?.data?.error || error.message || 'The source could not be approved.'
      } finally {
        this.approvingJobId = ''
      }
    },
    async resolveStreamHealth(resolution) {
      if (!this.selectedItem || this.resolvingStreamHealth) return
      this.resolvingStreamHealth = true
      this.detailError = ''
      this.actionMessage = ''
      try {
        const response = await axios.post(
          this.sourceInputUrl(this.selectedItem, '/stream-health/resolve'),
          { resolution }
        )
        const streamHealth = response.data && response.data.stream_health
        this.selectedDetail = {
          ...(this.selectedDetail || {}),
          settings: {
            ...((this.selectedDetail && this.selectedDetail.settings) || {}),
            stream_health: streamHealth || null,
          },
        }
        this.selectedItem = {
          ...this.selectedItem,
          stream_health: streamHealth || null,
        }
        this.actionMessage = resolution === 'source_replaced'
          ? 'Stream warning resolved after the source was replaced.'
          : 'Stream warning marked as reviewed.'
        await this.loadItems()
      } catch (error) {
        this.detailError = error.response?.data?.error || error.message || 'Could not resolve the stream warning.'
      } finally {
        this.resolvingStreamHealth = false
      }
    },
    approvalProcessingProfile() {
      if (this.approvalUsages.length === 1 && this.approvalUsages[0] === 'digital_download') return 'download_only'
      if (this.approvalUsages.includes('digital_download')) return 'clip_and_download'
      return 'clip_source'
    },
    clearRecordingPreview() {
      if (this.reviewPreviewUrl) {
        URL.revokeObjectURL(this.reviewPreviewUrl)
      }
      this.reviewPreviewJobId = ''
      this.reviewPreviewUrl = ''
      this.reviewPreviewLoadingJobId = ''
      this.reviewPreviewError = ''
    },
    async loadRecordingPreview(job) {
      if (!this.selectedItem || !job || !job.id) return
      if (this.reviewPreviewUrl) {
        URL.revokeObjectURL(this.reviewPreviewUrl)
      }
      this.reviewPreviewJobId = job.id
      this.reviewPreviewUrl = ''
      this.reviewPreviewError = ''
      this.reviewPreviewLoadingJobId = job.id
      try {
        let response = null
        for (let attempt = 0; attempt < 150; attempt += 1) {
          response = await axios.get(this.sourceInputUrl(this.selectedItem, `/jobs/${job.id}/preview`))
          if (response.status !== 202 && response.data && response.data.status !== 'preparing') break
          if (this.reviewPreviewJobId !== job.id) return
          await new Promise(resolve => window.setTimeout(resolve, 2000))
        }
        const playlist = response && response.data && response.data.playlist
        if (!playlist) throw new Error('Preview playlist was empty.')
        const blob = new Blob([playlist], {
          type: response.data.contentType || 'application/vnd.apple.mpegurl',
        })
        this.reviewPreviewUrl = URL.createObjectURL(blob)
      } catch (error) {
        if (this.reviewPreviewJobId === job.id) {
          this.reviewPreviewError = error.response?.data?.error || error.message || 'Could not load the recording preview.'
        }
      } finally {
        if (this.reviewPreviewLoadingJobId === job.id) {
          this.reviewPreviewLoadingJobId = ''
        }
      }
    },
    reviewPlayer() {
      const ref = this.$refs.reviewVideoPlayer
      if (Array.isArray(ref)) return ref[0] && ref[0].player
      return ref && ref.player
    },
    currentReviewTime() {
      const player = this.reviewPlayer()
      if (!player || typeof player.currentTime !== 'function') return null
      const seconds = player.currentTime()
      return Number.isFinite(seconds) ? seconds : null
    },
    markApprovalTiming(edge) {
      const seconds = this.currentReviewTime()
      if (seconds === null) {
        this.reviewPreviewError = 'Preview is not ready yet.'
        return
      }
      const value = String(Math.floor(seconds))
      if (edge === 'start') {
        this.approvalTiming.startSeconds = value
      } else {
        this.approvalTiming.endSeconds = value
      }
    },
    markApprovalReference(kind) {
      const seconds = this.currentReviewTime()
      if (seconds === null) {
        this.reviewPreviewError = 'Preview is not ready yet.'
        return
      }
      this.approvalReferences[kind] = String(Number(seconds.toFixed(2)))
    },
    resetApprovalFields() {
      this.approvalTiming = {
        startSeconds: '',
        endSeconds: '',
      }
      this.approvalReferences = {
        wide: '',
        close: '',
      }
    },
    numberOrNull(value) {
      if (value === '' || value === null || value === undefined) return null
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : null
    },
    approvalReferencesPayload() {
      return {
        wide: this.numberOrNull(this.approvalReferences.wide),
        close: this.numberOrNull(this.approvalReferences.close),
      }
    },
    approvalClipWindowPayload() {
      if (this.approvalTimingError) return null
      const start = this.numberOrNull(this.approvalTiming.startSeconds)
      const end = this.numberOrNull(this.approvalTiming.endSeconds)
      if (start === null && end === null) return null
      return {
        startSeconds: start,
        endSeconds: end,
      }
    },
    copyAvInstructions() {
      const lines = [
        `Ceremony: ${this.ceremonyLabel(this.selectedItem)}`,
        `Recording window: ${this.detailScheduleLabel}`,
        `Timezone: ${(this.selectedSettings && this.selectedSettings.schedule_timezone) || 'UTC'}`,
      ]
      Object.entries(this.ingestUrls).forEach(([protocol, url]) => {
        lines.push(`${protocol.toUpperCase()} ingest URL: ${url}`)
      })
      if (this.selectedSettings && this.selectedSettings.stream_key_hint) {
        if (this.selectedSettings.stream_key) {
          lines.push(`Stream key: ${this.selectedSettings.stream_key}`)
        } else {
          lines.push(`Stream key: NOT VISIBLE HERE - current key ends ${this.selectedSettings.stream_key_hint}`)
        }
      }
      if (this.streamKeyHidden) {
        lines.push('If the AV team does not already have the full key, rotate the key in MyGraduationClip before sending these instructions.')
      } else {
        lines.push('Full stream keys are shown only when first created or rotated in MyGraduationClip.')
      }
      this.copyText(lines.join('\n'), 'AV instructions copied')
    },
    copyError(item) {
      const text = item.latest_job?.error_message || ''
      if (!text) return
      this.copyText(text, 'Error copied')
    },
    copyText(text, message) {
      if (!text) return
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.actionMessage = message
        }).catch(() => {})
      }
    },
    reviewJobLabel(job) {
      if (job.job_type === 'ivs_recording') return 'Recorded stream from AV team'
      if (job.job_type === 'live_url') return 'Recorded live URL'
      if (job.job_type === 'published_video') return 'Imported video (legacy)'
      if (job.job_type === 'stream_recording') return 'Recorded stream'
      return 'Source'
    },
    reviewJobSummary(job) {
      const parts = []
      if (job.status) parts.push(this.rawStatusLabel(job.status))
      if (job.metadata && job.metadata.recording_duration_ms) parts.push(`Duration ${this.formatDurationMs(job.metadata.recording_duration_ms)}`)
      const timeZone = job.schedule_timezone || (this.selectedItem && this.selectedItem.schedule_timezone) || 'UTC'
      const time = this.formatDateTime(job.completed_at || job.started_at || job.updated_at || job.created_at, timeZone)
      if (time) parts.push(time)
      return parts.join(' · ') || 'Open details for technical status.'
    },
    jobTechnicalSummary(job) {
      const timeZone = job.schedule_timezone || (this.selectedItem && this.selectedItem.schedule_timezone) || 'UTC'
      const start = this.formatDateTime(job.scheduled_start_at || job.started_at, timeZone)
      const end = this.formatDateTime(job.scheduled_end_at || job.completed_at, timeZone)
      if (start && end) return `${start} to ${end}`
      if (start) return start
      if (end) return end
      return job.id || ''
    },
    jobTypeLabel(value) {
      const labels = {
        live_url: 'Live URL',
        published_video: 'Published video (legacy)',
        stream_recording: 'Stream recording',
        ivs_recording: 'IVS recording',
      }
      return labels[value] || value || 'Job'
    },
    rawStatusLabel(status) {
      const labels = {
        not_configured: 'Not configured',
        configured: 'Configured',
        ready_for_encoder: 'Ready for encoder',
        needs_attention: 'Needs attention',
        queued: 'Queued',
        dispatched: 'Dispatched',
        running: 'Running',
        completed: 'Completed',
        available: 'Available',
        selected: 'Selected',
        failed: 'Failed',
      }
      return labels[status] || status || 'Unknown'
    },
    rawStatusClass(status) {
      const base = 'inline-flex rounded px-2 py-1 text-xs font-medium'
      if (status === 'needs_attention' || status === 'failed') return `${base} bg-red-100 text-red-800`
      if (status === 'available') return `${base} bg-yellow-100 text-yellow-800`
      if (status === 'completed' || status === 'selected') return `${base} bg-green-100 text-green-800`
      if (status === 'running' || status === 'dispatched') return `${base} bg-blue-100 text-blue-800`
      return `${base} bg-gray-100 text-gray-800`
    },
    formatDurationMs(value) {
      const totalSeconds = Math.max(Math.floor(Number(value || 0) / 1000), 0)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      if (hours) return `${hours}h ${minutes}m ${seconds}s`
      if (minutes) return `${minutes}m ${seconds}s`
      return `${seconds}s`
    },
    formatDateTime(value, timeZone = 'UTC') {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      try {
        return new Intl.DateTimeFormat(undefined, {
          timeZone: timeZone || 'UTC',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
        }).format(date)
      } catch {
        return date.toLocaleString()
      }
    },
  },
}
</script>
